<template>
  <div class="three-steps">
    <div class="three-steps__container">
      <div class="three-steps__container__cta">
        <h1
          class="three-steps__container__cta--title"
          v-html="$t('b2b__landing_page__landing_3_steps__title')"
        />
        <BaseButton
          class="cta"
          @click="handleCTAButtonClick"
        >
          {{ ctaBtnTitle }}
        </BaseButton>
      </div>
      <div class="three-steps__container__steps">
        <div
          v-for="({ image, title }, index) in cards"
          :key="index"
          class="three-steps__container__steps--step"
        >
          <img
            :src="image"
            :alt="`step ${index}`"
            class="three-steps__container__steps--step--icon"
          />
          <p
            class="three-steps__container__steps--step--title"
            v-html="$t(title)"
          />
        </div>
      </div>

      <BaseButton
        class="cta mobile-cta"
        @click="handleCTAButtonClick"
      >
        {{ ctaBtnTitle }}
      </BaseButton>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import BaseButton from '@shared/components/BaseButton.vue'
import accountSetupImage from '@shared/assets/img/account_setup.svg'
import downloadImage from '@shared/assets/img/download.svg'
import firstDayImage from '@shared/assets/img/first_day.svg'
import checkupImage from '@shared/assets/img/checkup.svg'
import env from '@shared/env.js'

defineProps({
  handleCTAButtonClick: {
    type: Function,
    default: () => {},
  },
  ctaBtnTitle: {
    type: String,
    default: '',
  },
})

const cards = computed(() =>
  [
    {
      image: accountSetupImage,
      title: 'b2b__landing_page__landing_3_steps__card_one__title',
    },
    {
      image: downloadImage,
      title: 'b2b__landing_page__landing_3_steps__card_two__title',
    },
    {
      image: env.isMsk ? firstDayImage : checkupImage,
      title: 'b2b__landing_page__landing_3_steps__card_three__title',
    },
    env.isCopd && {
      image: firstDayImage,
      title: 'b2b__landing_page__landing_3_steps__card_four__title',
    },
  ].filter(Boolean),
)
</script>

<style lang="scss" scoped>
.three-steps {
  padding: 40px 10px;
  background: $kds-color-background-green-subtle;

  @include media-up(lg) {
    padding: $kds-space-12x 10px;
  }

  .cta {
    width: fit-content;
    margin: 31px 0;
    display: none;

    @include media-up(md) {
      display: flex;
    }

    &.mobile-cta {
      display: flex;
      margin: 31px auto;

      @include media-up(md) {
        display: none;
      }
    }
  }

  &__container {
    @extend %container;
    margin: 0 auto;
    @include flex-container(column, flex-start, md);

    @include media-up(md) {
      display: grid;
      grid-template-columns: 6.67% minmax(0, 40%) 6.66% minmax(0, 40%) 6.67%;
      gap: 0;
      align-items: center;
    }

    &__cta {
      @include media-up(md) {
        grid-column: 2;
        width: fit-content;
        max-width: 100%;
        justify-self: center;
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .cta {
        @include media-up(md) {
          margin: 31px auto 0;
        }
      }

      &--title {
        text-align: center;
        margin-bottom: $kds-space-6x;
        display: block;
        @include kds-typography-display-small;
        font-weight: $kds-typography-font-weight-medium;

        @include media-up(md) {
          @include kds-typography-display-medium;
          font-weight: $kds-typography-font-weight-medium;
          font-size: 2rem;
          line-height: 2.4rem;
          text-align: center;
          margin-bottom: 0;
          max-width: none;
        }
      }

      &--button {
        display: none;

        @include media-up(md) {
          display: block;
        }
      }
    }

    &__steps {
      --three-steps-icon-size: 60px;
      --three-steps-row-gap: 70px;
      --three-steps-icon-line-gap: 8px;
      display: flex;
      flex-direction: column;
      margin: auto;
      padding: 0 50px;
      grid-gap: var(--three-steps-row-gap);
      max-width: 450px;

      @include media-up(md) {
        grid-column: 4;
        width: 100%;
        padding: 0;
        margin: 0;
        max-width: none;
      }

      &--step {
        display: flex;
        grid-gap: 30px;
        align-items: start;
        position: relative;

        // Connector between consecutive steps with a fixed 8px gap to icons.
        &:not(:last-child):after {
          position: absolute;
          content: '';
          width: 2px;
          height: calc(
            100% + var(--three-steps-row-gap) - var(--three-steps-icon-size) -
              (2 * var(--three-steps-icon-line-gap))
          );
          background: $kds-color-background-accent;
          left: calc((var(--three-steps-icon-size) - 2px) / 2);
          top: calc(
            var(--three-steps-icon-size) + var(--three-steps-icon-line-gap)
          );
        }

        &--title {
          margin: 2px 0 0 0;
        }

        &--icon {
          height: var(--three-steps-icon-size);
          width: var(--three-steps-icon-size);
        }
      }
    }

    &__mobile-cta {
      margin-top: $kds-space-2_5x;
      display: flex;
      justify-content: center;

      @include media-up(md) {
        display: none;
      }
    }
  }
}
</style>
